/**
 * The widget renders inside a Shadow DOM, so its styles are isolated
 * from the host site. Exposing CSS variables on `:host` lets merchants
 * theme the widget without touching the bundle.
 *
 * The default palette honours ``prefers-color-scheme`` so the widget
 * blends in with both light and dark host pages out of the box. A
 * merchant who pins a colour scheme via ``theme.mode`` overrides the
 * preference (we set ``data-mode="dark"|"light"`` on ``:host``).
 */
export const SHADOW_CSS = `
:host {
  --ts-primary: #c12944;
  --ts-primary-fg: #ffffff;
  --ts-bg: #ffffff;
  --ts-fg: #0f172a;
  --ts-muted: #64748b;
  --ts-border: #e2e8f0;
  --ts-rate-active-bg: rgba(193, 41, 68, 0.06);
  --ts-error-bg: #fee2e2;
  --ts-error-fg: #991b1b;
  --ts-success-bg: #dcfce7;
  --ts-success-fg: #166534;
  --ts-warn-bg: #fef3c7;
  --ts-warn-fg: #92400e;
  --ts-radius: 8px;
  --ts-font: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  display: block;
  color-scheme: light dark;
  font-family: var(--ts-font);
  color: var(--ts-fg);
  box-sizing: border-box;
}

@media (prefers-color-scheme: dark) {
  :host(:not([data-mode="light"])) {
    --ts-bg: #0f172a;
    --ts-fg: #f8fafc;
    --ts-muted: #94a3b8;
    --ts-border: #1e293b;
    --ts-rate-active-bg: rgba(193, 41, 68, 0.18);
    --ts-error-bg: rgba(239, 68, 68, 0.18);
    --ts-error-fg: #fecaca;
    --ts-success-bg: rgba(34, 197, 94, 0.18);
    --ts-success-fg: #bbf7d0;
    --ts-warn-bg: rgba(245, 158, 11, 0.18);
    --ts-warn-fg: #fde68a;
  }
}

:host([data-mode="dark"]) {
  --ts-bg: #0f172a;
  --ts-fg: #f8fafc;
  --ts-muted: #94a3b8;
  --ts-border: #1e293b;
  --ts-rate-active-bg: rgba(193, 41, 68, 0.18);
  --ts-error-bg: rgba(239, 68, 68, 0.18);
  --ts-error-fg: #fecaca;
  --ts-success-bg: rgba(34, 197, 94, 0.18);
  --ts-success-fg: #bbf7d0;
  --ts-warn-bg: rgba(245, 158, 11, 0.18);
  --ts-warn-fg: #fde68a;
}

* { box-sizing: border-box; }

.tsw {
  background: var(--ts-bg);
  border: 1px solid var(--ts-border);
  border-radius: var(--ts-radius);
  padding: 16px;
  font-size: 14px;
  line-height: 1.45;
}

.tsw-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.tsw-mono { font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; font-size: 12px; }
.tsw-muted { color: var(--ts-muted); }
.tsw-title { font-weight: 600; font-size: 16px; }
.tsw-h6 { font-weight: 600; margin: 8px 0; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; }

.tsw-input {
  width: 100%; padding: 8px 10px; border-radius: 6px;
  border: 1px solid var(--ts-border); font: inherit;
  background: var(--ts-bg); color: var(--ts-fg);
}
.tsw-input:focus-visible {
  outline: 2px solid var(--ts-primary); outline-offset: 2px;
}
.tsw-button {
  background: var(--ts-primary); color: var(--ts-primary-fg);
  border: none; border-radius: 6px; padding: 8px 14px; font-weight: 600;
  cursor: pointer; font: inherit;
}
.tsw-button:focus-visible {
  outline: 2px solid var(--ts-primary); outline-offset: 2px;
}
.tsw-button[disabled] { opacity: .6; cursor: not-allowed; }
.tsw-button-outline {
  background: transparent; color: var(--ts-fg);
  border: 1px solid var(--ts-border);
}

.tsw-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; font-size: 12px; border-radius: 999px;
  border: 1px solid var(--ts-border);
}
.tsw-badge-success { background: var(--ts-success-bg); color: var(--ts-success-fg); border-color: transparent; }
.tsw-badge-warn { background: var(--ts-warn-bg); color: var(--ts-warn-fg); border-color: transparent; }
.tsw-badge-danger { background: var(--ts-error-bg); color: var(--ts-error-fg); border-color: transparent; }

.tsw-timeline { list-style: none; padding-left: 16px; border-left: 2px solid var(--ts-border); margin: 16px 0 0; }
.tsw-timeline li { position: relative; padding-bottom: 12px; }
.tsw-timeline li::before {
  content: ''; position: absolute; left: -22px; top: 6px; width: 10px; height: 10px;
  border-radius: 50%; background: var(--ts-primary);
}

.tsw-rate {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 10px 12px; border: 1px solid var(--ts-border);
  border-radius: 8px; cursor: pointer; background: var(--ts-bg);
  font: inherit; color: inherit; text-align: start; width: 100%;
}
.tsw-rate:focus-visible {
  outline: 2px solid var(--ts-primary); outline-offset: 2px;
}
.tsw-rate.tsw-rate-active { border-color: var(--ts-primary); background: var(--ts-rate-active-bg); }

.tsw-error {
  background: var(--ts-error-bg); color: var(--ts-error-fg);
  padding: 8px 12px; border-radius: 6px; font-size: 13px;
}
.tsw-loading::before {
  content: ''; display: inline-block; width: 12px; height: 12px;
  border: 2px solid currentColor; border-top-color: transparent;
  border-radius: 50%; animation: tsw-spin 0.8s linear infinite;
  vertical-align: -2px; margin-right: 8px;
}
@keyframes tsw-spin { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .tsw-loading::before { animation: none; }
  * { transition-duration: 0.001ms !important; animation-duration: 0.001ms !important; }
}

.tsw-checkout-summary {
  border: 1px solid var(--ts-border); border-radius: 8px;
  padding: 12px; display: flex; align-items: center;
  justify-content: space-between; gap: 12px; margin-top: 12px;
}

.tsw-actions { display: flex; gap: 8px; flex-wrap: wrap; }

[dir="rtl"] .tsw-timeline { border-left: none; border-right: 2px solid var(--ts-border); padding-right: 16px; padding-left: 0; }
[dir="rtl"] .tsw-timeline li::before { left: auto; right: -22px; }

.tsw-sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}
`;
